<template>
  <div class="cf-report" :style="themeVars">
    <div v-if="summary" class="cf-summary">
      <div class="cf-summary__item">
        <div class="cf-summary__label">Total Uang Masuk</div>
        <div class="cf-summary__value">{{ formatAmount(summary.total_uang_masuk) }}</div>
      </div>
      <div class="cf-summary__item">
        <div class="cf-summary__label">Total Uang Keluar</div>
        <div class="cf-summary__value">{{ formatAmount(summary.total_uang_keluar) }}</div>
      </div>
      <div class="cf-summary__item cf-summary__item--net">
        <div class="cf-summary__label">Total Cashflow</div>
        <div class="cf-summary__value">{{ formatAmount(summary.net_cashflow) }}</div>
      </div>
    </div>

    <div class="cf-toolbar">
      <div class="cf-toolbar__group">
        <button
          type="button"
          class="cf-btn"
          @click="expandAll">
          <v-icon size="14">mdi-arrow-expand-vertical</v-icon>
          Buka Semua
        </button>

        <button
          type="button"
          class="cf-btn"
          @click="collapseAll">
          <v-icon size="14">mdi-arrow-collapse-vertical</v-icon>
          Tutup Semua
        </button>

        <button
          type="button"
          class="cf-btn"
          :class="{ 'cf-btn--active': hideEmptyColumns }"
          :disabled="!emptyColumnCount"
          @click="hideEmptyColumns = !hideEmptyColumns">
          <v-icon size="14">mdi-table-column-remove</v-icon>
          Sembunyikan Kolom Kosong
          <span v-if="emptyColumnCount" class="cf-btn__badge">{{ emptyColumnCount }}</span>
        </button>
      </div>

      <!-- <div class="cf-toolbar__group">
        <div class="cf-search">
          <v-icon size="15" color="#6b7280">mdi-magnify</v-icon>
          <input
            v-model="search"
            type="text"
            class="cf-search__input"
            placeholder="Cari No. / Nama COA">
          <button
            v-if="search"
            type="button"
            class="cf-search__clear"
            @click="search = ''">
            <v-icon size="14">mdi-close-circle</v-icon>
          </button>
        </div>
      </div> -->
    </div>

    <div v-if="isSearching" class="cf-hint">
      <v-icon size="14" color="#8a6d1f">mdi-information-outline</v-icon>
      Menampilkan {{ matchCount }} COA yang cocok dengan "{{ search }}". Baris subtotal &amp; total disembunyikan selama pencarian.
    </div>

    <div class="cf-scroll">
      <table class="cf-table">
        <thead>
          <tr>
            <th class="cf-th cf-sticky cf-sticky--1 cf-col-coa">No. COA</th>
            <th class="cf-th cf-sticky cf-sticky--2 cf-col-name">Nama COA</th>
            <th
              v-for="header in visibleHeaders"
              :key="headerId(header)"
              class="cf-th cf-col-sumber cf-th--tooltip"
              :data-tooltip="header.cashflow_sumber_coa_name">
              <span class="cf-th__code">{{ header.cashflow_sumber_code }}</span>
              <!-- <span class="cf-th__name">{{ header.cashflow_sumber_coa_name }}</span> -->
            </th>
            <th class="cf-th cf-col-total">Grand Total</th>
            <th class="cf-th cf-col-trx">Jumlah Transaksi</th>
          </tr>
        </thead>

        <tbody>
          <!-- Saldo Awal Kas -->
          <tr v-if="showSaldo && saldoAwal" class="cf-row--saldo cf-row--saldo-awal">
            <td class="cf-td cf-sticky cf-sticky--1 cf-col-coa cf-td--label text-uppercase" :colspan="2">
              {{ saldoAwal.label || 'Saldo Awal Kas' }}
              <span v-if="saldoAwal.as_of_date" class="cf-saldo-date">(Saldo Akhir Kas Bulan Sebelumnya)</span>
            </td>
            <td
              v-for="header in visibleHeaders"
              :key="`saldo-awal-${headerId(header)}`"
              class="cf-td cf-col-sumber cf-num"
              :class="{ 'cf-num--zero': !saldoNominal('saldo_awal_kas_array', header) }">
              {{ displayAmount(saldoNominal('saldo_awal_kas_array', header)) }}
            </td>
            <td class="cf-td cf-col-total cf-num cf-num--bold">
              {{ formatAmount(saldoAwal.total_nominal) }}
            </td>
            <td class="cf-td cf-col-trx cf-num cf-num--zero">–</td>
          </tr>

          <tr
            v-for="row in tableRows"
            :key="row.id"
            :class="rowClass(row)">
            <!-- Category / Type section labels — collapsible -->
            <template v-if="row.kind === 'category' || row.kind === 'type'">
              <td
                class="cf-td cf-sticky cf-sticky--1 cf-col-coa cf-td--section"
                :colspan="2"
                @click="toggle(row)">
                <span class="cf-node">
                  <v-icon class="cf-node__chevron" size="16">
                    {{ isCollapsed(row.id) ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
                  </v-icon>
                  <span class="cf-node__label">{{ row.label }}</span>
                  <span class="cf-node__count">{{ row.detail_count }} COA</span>
                </span>
              </td>
              <td
                v-for="header in visibleHeaders"
                :key="`${row.id}-${headerId(header)}`"
                class="cf-td cf-col-sumber cf-num"
                :class="{ 'cf-num--zero': !footerNominal(row, header) }">
                <template v-if="isCollapsed(row.id)">
                  {{ displayAmount(footerNominal(row, header)) }}
                </template>
              </td>
              <td class="cf-td cf-col-total cf-num cf-num--bold">
                <template v-if="isCollapsed(row.id)">{{ formatAmount(row.grand_total) }}</template>
              </td>
              <td class="cf-td cf-col-trx cf-num cf-num--bold">
                <template v-if="isCollapsed(row.id)">{{ formatAmount(row.total_transaksi) }}</template>
              </td>
            </template>

            <!-- Detail -->
            <template v-else-if="row.kind === 'detail'">
              <td class="cf-td cf-sticky cf-sticky--1 cf-col-coa cf-td--indent">{{ row.coa_number }}</td>
              <td class="cf-td cf-sticky cf-sticky--2 cf-col-name">{{ row.coa_name }}</td>
              <td
                v-for="header in visibleHeaders"
                :key="`${row.id}-${headerId(header)}`"
                class="cf-td cf-col-sumber cf-num"
                :class="{ 'cf-num--zero': !sumberNominal(row, header) }">
                {{ displayAmount(sumberNominal(row, header)) }}
              </td>
              <td class="cf-td cf-col-total cf-num cf-num--bold">
                {{ formatAmount(row.grand_total) }}
              </td>
              <td class="cf-td cf-col-trx cf-num">
                {{ formatAmount(row.total_transaksi) }}
              </td>
            </template>

            <!-- Subtotal / Category Total -->
            <template v-else-if="row.kind === 'subtotal' || row.kind === 'category_total'">
              <td
                class="cf-td cf-sticky cf-sticky--1 cf-col-coa cf-td--label"
                :class="{ 'cf-td--label-wrap': row.wrap_label }"
                :colspan="2">
                {{ row.label }}
              </td>
              <td
                v-for="header in visibleHeaders"
                :key="`${row.id}-${headerId(header)}`"
                class="cf-td cf-col-sumber cf-num"
                :class="{ 'cf-num--zero': row.values && !footerNominal(row, header) }">
                <template v-if="row.values">
                  {{ displayAmount(footerNominal(row, header)) }}
                </template>
              </td>
              <td class="cf-td cf-col-total cf-num cf-num--bold">
                {{ formatAmount(row.grand_total) }}
              </td>
              <td class="cf-td cf-col-trx cf-num cf-num--bold">
                {{ formatAmount(row.total_transaksi) }}
              </td>
            </template>

            <!-- Footer by sumber -->
            <template v-else-if="row.kind === 'footer' || row.kind === 'footer_total'">
              <td
                class="cf-td cf-sticky cf-sticky--1 cf-col-coa cf-td--label"
                :colspan="2">
                {{ row.label }}
              </td>
              <td
                v-for="header in visibleHeaders"
                :key="`${row.id}-${headerId(header)}`"
                class="cf-td cf-col-sumber cf-num"
                :class="{ 'cf-num--zero': !footerNominal(row, header) }">
                {{ displayAmount(footerNominal(row, header)) }}
              </td>
              <td class="cf-td cf-col-total cf-num cf-num--bold">
                {{ formatAmount(row.grand_total) }}
              </td>
              <td class="cf-td cf-col-trx cf-num cf-num--bold">
                {{ formatAmount(row.total_transaksi) }}
              </td>
            </template>
          </tr>

          <!-- Saldo Akhir Kas (menggantikan Total Per Sumber Dana) -->
          <tr v-if="showSaldo && saldoAkhir" class="cf-row--saldo cf-row--saldo-akhir">
            <td class="cf-td cf-sticky cf-sticky--1 cf-col-coa cf-td--label" :colspan="2">
              {{ saldoAkhir.label || 'Saldo Akhir Kas' }}
            </td>
            <td
              v-for="header in visibleHeaders"
              :key="`saldo-akhir-${headerId(header)}`"
              class="cf-td cf-col-sumber cf-num"
              :class="{ 'cf-num--zero': !saldoNominal('saldo_akhir_kas_array', header) }">
              {{ displayAmount(saldoNominal('saldo_akhir_kas_array', header)) }}
            </td>
            <td class="cf-td cf-col-total cf-num cf-num--bold">
              {{ formatAmount(saldoAkhir.total_nominal) }}
            </td>
            <td class="cf-td cf-col-trx cf-num cf-num--zero">–</td>
          </tr>

          <tr v-if="!tableRows.length">
            <td
              class="cf-td cf-empty"
              :colspan="emptyColspan">
              {{ isSearching ? `COA "${search}" tidak ditemukan` : 'Belum Ada Data Laporan Konsolidasi Kas' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="sumberDanaSummary" class="cf-sumber-summary-wrap">
      <div class="cf-sumber-summary">
        <div class="cf-sumber-summary__title">Summary</div>

        <v-divider class="my-2"></v-divider>

        <div class="cf-sumber-summary__group">
          <div class="cf-sumber-summary__heading">Uang Masuk</div>
          <div class="cf-sumber-summary__row">
            <span class="cf-sumber-summary__label">Total Uang Masuk di Sumber Dana Kas Kecil</span>
            <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_masuk.kas.total_nominal) }}</span>
          </div>
          <div class="cf-sumber-summary__row">
            <span class="cf-sumber-summary__label">Total Uang Masuk di Sumber Dana Bank</span>
            <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_masuk.bank.total_nominal) }}</span>
          </div>
          <div class="cf-sumber-summary__row cf-sumber-summary__row--total">
            <span class="cf-sumber-summary__label">Total Uang Masuk Keseluruhan</span>
            <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_masuk.total.total_nominal) }}</span>
          </div>
        </div>

        <v-divider class="my-2"></v-divider>

        <div class="cf-sumber-summary__group">
          <div class="cf-sumber-summary__heading">Uang Keluar</div>
          <div class="cf-sumber-summary__row">
            <span class="cf-sumber-summary__label">Total Uang Keluar di Sumber Dana Kas Kecil</span>
            <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_keluar.kas.total_nominal) }}</span>
          </div>
          <div class="cf-sumber-summary__row">
            <span class="cf-sumber-summary__label">Total Uang Keluar di Sumber Dana Bank</span>
            <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_keluar.bank.total_nominal) }}</span>
          </div>
          <div class="cf-sumber-summary__row cf-sumber-summary__row--total pb-4">
            <span class="cf-sumber-summary__label">Total Uang Keluar Keseluruhan</span>
            <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_keluar.total.total_nominal) }}</span>
          </div>
        </div>
      </div>

      <div class="cf-sumber-summary-net">
        <div class="cf-sumber-summary-net__label">Total Cashflow</div>
        <div class="cf-sumber-summary-net__value">{{ formatAmount(sumberDanaNetKeseluruhan) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import {
    buildTableRows,
    buildFooterRows,
    buildSumberDanaSummary,
    collectGroupIds,
    defaultCollapsedIds,
    filterCollapsedRows,
    filterRowsByQuery,
    formatAmount,
    headerLabel,
    headerId,
    getSumberNominal,
    footerValueForHeader
  } from '@/utils/cashflowReport'

  const STICKY_COA_WIDTH = 110
  const STICKY_NAME_WIDTH = 320

  export default {
    name: 'CashflowReportTable',
    props: {
      rekapitulasi: {
        type: Object,
        default: null
      },
      kategori: {
        type: Object,
        default: null
      },
      summary: {
        type: Object,
        default: null
      },
      color: {
        type: String,
        default: '#1976D2'
      }
    },
    data () {
      return {
        collapsed: [],
        search: '',
        hideEmptyColumns: false
      }
    },
    computed: {
      headers () {
        const list = (this.rekapitulasi && this.rekapitulasi.header_sumber_dana_array) || []
        return [...list].sort((a, b) => this.headerSortValue(a) - this.headerSortValue(b))
      },
      allRows () {
        if (!this.rekapitulasi) return []
        const body = buildTableRows(this.rekapitulasi.group_array, this.kategori)
        let footer = buildFooterRows(this.rekapitulasi)
        // "Total Per Sumber Dana" diganti baris Saldo Akhir Kas bila API mengirimnya
        if (this.saldoAkhir) footer = footer.filter(row => row.kind !== 'footer_total')
        return body.concat(footer)
      },
      saldoAwal () {
        return (this.rekapitulasi && this.rekapitulasi.saldo_awal_kas) || null
      },
      saldoAkhir () {
        return (this.rekapitulasi && this.rekapitulasi.saldo_akhir_kas) || null
      },
      showSaldo () {
        return !this.isSearching && this.tableRows.length > 0
      },
      isSearching () {
        return !!String(this.search || '').trim()
      },
      tableRows () {
        const rows = this.isSearching
          ? filterRowsByQuery(this.allRows, this.search)
          : filterCollapsedRows(this.allRows, this.collapsed)

        let stripe = 0

        return rows.map(row => {
          if (row.kind !== 'detail') {
            stripe = 0
            return row
          }

          stripe += 1
          return { ...row, stripe: stripe % 2 === 0 }
        })
      },
      matchCount () {
        return this.tableRows.filter(row => row.kind === 'detail').length
      },
      /** Absolute nominal per sumber column, used to detect empty columns. */
      columnWeights () {
        const weights = {}

        const accumulate = list => {
          ;(list || []).forEach(item => {
            const id = item.cashflow_sumber_coa_id
            if (id == null) return

            weights[id] = (weights[id] || 0) + Math.abs(Number(item.total_nominal) || 0)
          })
        }

        this.allRows.forEach(row => {
          accumulate(row.nominal_by_sumber_array)
          accumulate(row.values)
        })

        // kolom yang punya saldo tidak boleh dianggap kosong
        if (this.rekapitulasi) {
          accumulate(this.rekapitulasi.saldo_awal_kas_array)
          accumulate(this.rekapitulasi.saldo_akhir_kas_array)
        }

        return weights
      },
      emptyColumnCount () {
        return this.headers.filter(header => !this.columnWeights[headerId(header)]).length
      },
      visibleHeaders () {
        if (!this.hideEmptyColumns) return this.headers
        return this.headers.filter(header => !!this.columnWeights[headerId(header)])
      },
      sumberDanaSummary () {
        return buildSumberDanaSummary(this.rekapitulasi)
      },
      sumberDanaNetKeseluruhan () {
        if (this.summary && this.summary.net_cashflow != null) {
          return Number(this.summary.net_cashflow) || 0
        }

        const summary = this.sumberDanaSummary
        if (!summary) return 0

        const masuk = Number(summary.uang_masuk.total.total_nominal) || 0
        const keluar = Number(summary.uang_keluar.total.total_nominal) || 0
        return masuk - keluar
      },
      emptyColspan () {
        return 2 + this.visibleHeaders.length + 2
      },
      themeVars () {
        return {
          '--cf-accent': this.color || '#1976D2',
          '--cf-sticky-1': `${STICKY_COA_WIDTH}px`,
          '--cf-sticky-2-left': `${STICKY_COA_WIDTH}px`,
          '--cf-sticky-2': `${STICKY_NAME_WIDTH}px`
        }
      }
    },
    watch: {
      rekapitulasi: {
        immediate: true,
        handler () {
          this.applyDefaultCollapse()
        }
      }
    },
    methods: {
      applyDefaultCollapse () {
        this.collapsed = defaultCollapsedIds(this.allRows)
      },
      formatAmount,
      headerLabel,
      headerId,
      displayAmount (value) {
        return Number(value) ? formatAmount(value) : '–'
      },
      headerSortValue (header) {
        const code = (header && header.cashflow_sumber_code) || ''
        const match = code.match(/(\d+)/)

        return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER
      },
      formatDate (value) {
        return moment(value).format('DD MMM YYYY')
      },
      saldoNominal (key, header) {
        const list = (this.rekapitulasi && this.rekapitulasi[key]) || []
        const id = headerId(header)
        const found = list.find(item => String(item.cashflow_sumber_coa_id) === String(id))

        return found ? Number(found.total_nominal) || 0 : 0
      },
      isCollapsed (id) {
        if (this.isSearching) return false
        return this.collapsed.indexOf(id) !== -1
      },
      toggle (row) {
        if (!row.group) return

        const index = this.collapsed.indexOf(row.id)

        if (index === -1) this.collapsed.push(row.id)
        else this.collapsed.splice(index, 1)
      },
      expandAll () {
        this.collapsed = []
      },
      collapseAll () {
        this.collapsed = collectGroupIds(this.allRows)
      },
      rowClass (row) {
        return [
          `cf-row--${row.kind}`,
          row.type_key ? `cf-row--${row.type_key}` : null,
          row.group ? 'cf-row--group' : null,
          row.group && this.isCollapsed(row.id) ? 'cf-row--collapsed' : null,
          row.stripe ? 'cf-row--stripe' : null
        ]
      },
      sumberNominal (row, header) {
        return getSumberNominal(
          row.nominal_by_sumber_array,
          headerId(header)
        ).total_nominal
      },
      footerNominal (row, header) {
        return footerValueForHeader(row, headerId(header)).total_nominal
      }
    }
  }
</script>

<style scoped>
  .cf-report {
    --cf-bg: #ffffff;
    --cf-bg-muted: #f7f8fa;
    --cf-bg-head: var(--cf-accent);
    --cf-bg-category: #dde7f2;
    --cf-bg-type: #eef2f7;
    --cf-bg-subtotal: #eef6ee;
    --cf-bg-cat-total: #e3edf7;
    --cf-bg-footer: #f5f0e6;
    --cf-bg-footer-total: #e8e4dc;
    --cf-bg-saldo-awal: #e6eef7;
    --cf-bg-stripe: #fafbfc;
    --cf-border: #e0e3e8;
    --cf-text: #1f2933;
    --cf-text-muted: #6b7280;
    --cf-head-text: #ffffff;
    --cf-sticky-bg: #ffffff;
    --cf-hover: #eaf1fb;
    --cf-zero: #b6bcc4;

    color: var(--cf-text);
    background: var(--cf-bg);
  }

  /* —— Summary chips —— */
  .cf-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }

  .cf-summary__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    border-radius: 6px;
    background: var(--cf-bg-muted);
    border: 1px solid var(--cf-border);
    min-width: 180px;
  }

  .cf-summary__item--net {
    background: var(--cf-bg-cat-total);
    border-color: #c9d7e6;
    color: #1976D2;
  }

  .cf-summary__label {
    font-size: 11px;
    font-weight: 600;
    color: var(--cf-text-muted);
  }

  .cf-summary__value {
    font-size: 13px;
    font-weight: 800;
    /* font-variant-numeric: tabular-nums;
    color: var(--cf-text); */
  }

  /* .cf-summary__item--in .cf-summary__value {
    color: #2e7d32;
  }

  .cf-summary__item--out .cf-summary__value {
    color: #c62828;
  }

  .cf-summary__item--net .cf-summary__value {
    color: #1565c0;
  }

  .cf-summary__item--minus .cf-summary__value {
    color: #c62828;
  } */

  /* —— Toolbar —— */
  .cf-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
  }

  .cf-toolbar__group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }

  .cf-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border: 1px solid var(--cf-border);
    border-radius: 6px;
    background: var(--cf-bg);
    color: var(--cf-text);
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease;
  }

  .cf-btn:hover:not(:disabled) {
    background: var(--cf-bg-muted);
    border-color: #c6ccd4;
  }

  .cf-btn:disabled {
    opacity: 0.45;
    cursor: default;
  }

  .cf-btn--active {
    background: var(--cf-bg-cat-total);
    border-color: #a9c4e0;
    color: #1565c0;
  }

  .cf-btn__badge {
    padding: 1px 5px;
    border-radius: 8px;
    background: var(--cf-bg-muted);
    color: var(--cf-text-muted);
    font-size: 10px;
  }

  .cf-btn--active .cf-btn__badge {
    background: #ffffff;
    color: #1565c0;
  }

  .cf-search {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 9px;
    border: 1px solid var(--cf-border);
    border-radius: 6px;
    background: var(--cf-bg);
  }

  .cf-search:focus-within {
    border-color: var(--cf-accent);
  }

  .cf-search__input {
    width: 190px;
    border: none;
    outline: none;
    font-size: 11px;
    line-height: 18px;
    color: var(--cf-text);
    background: transparent;
  }

  .cf-search__clear {
    display: inline-flex;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
  }

  .cf-hint {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 8px;
    padding: 6px 10px;
    border-radius: 6px;
    background: #fdf7e3;
    border: 1px solid #efe0b0;
    color: #8a6d1f;
    font-size: 11px;
  }

  /* —— Sumber dana summary —— */
  .cf-sumber-summary-wrap {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 24px;
    margin-top: 16px;
  }

  .cf-sumber-summary {
    border: 1px solid var(--cf-border);
    border-radius: 8px;
    width: 350px;
    flex-shrink: 0;
  }

  .cf-sumber-summary-net {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
    padding: 8px 4px;
  }

  .cf-sumber-summary-net__label {
    font-size: 12px;
    font-weight: 600;
    color: var(--cf-text-muted);
    margin-bottom: 4px;
  }

  .cf-sumber-summary-net__value {
    font-size: 28px;
    font-weight: 800;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
    color: var(--cf-text);
    letter-spacing: -0.02em;
  }

  .cf-sumber-summary__title {
    font-size: 12px;
    font-weight: 700;
    color: var(--cf-text);
    margin-bottom: 10px;
    letter-spacing: 0.02em;
    padding: 8px 8px 0 8px;
  }

  .cf-sumber-summary__group {
    margin-bottom: 12px;
    padding: 0 8px 0 8px;
    padding-bottom: 0;
  }

  .cf-sumber-summary__group:last-child {
    margin-bottom: 0;
  }

  .cf-sumber-summary__heading {
    font-size: 11px;
    font-weight: 700;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .cf-sumber-summary__row {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 12px;
    align-items: baseline;
    padding: 4px 0;
    font-size: 11px;
    line-height: 1.4;
    border-bottom: 1px solid #f0f0f0;
  }

  .cf-sumber-summary__row:last-child {
    border-bottom: none;
  }

  .cf-sumber-summary__row--total {
    font-weight: 700;
    margin-top: 2px;
    padding-top: 6px;
  }

  .cf-sumber-summary__label {
    color: var(--cf-text);
    min-width: 0;
    word-break: break-word;
  }

  .cf-sumber-summary__value {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    text-align: right;
    white-space: nowrap;
  }

  .cf-sumber-summary__trx {
    font-variant-numeric: tabular-nums;
    color: var(--cf-text-muted);
    text-align: right;
    white-space: nowrap;
    min-width: 48px;
  }

  /* —— Scroll shell —— */
  .cf-scroll {
    overflow: auto;
    max-height: calc(100vh - 260px);
    border: 1px solid var(--cf-border);
    border-radius: 10px;
    background: var(--cf-bg);
    -webkit-overflow-scrolling: touch;
  }

  .cf-table {
    border-collapse: separate;
    border-spacing: 0;
    width: max-content;
    min-width: 100%;
    font-size: 12px;
    line-height: 1.4;
  }

  .cf-th,
  .cf-td {
    border-bottom: 1px solid var(--cf-border);
    border-right: 1px solid var(--cf-border);
    padding: 7px 10px;
    vertical-align: middle;
    white-space: nowrap;
  }

  .cf-th {
    position: sticky;
    top: 0;
    z-index: 4;
    background: var(--cf-bg-head) !important;
    color: var(--cf-head-text);
    font-weight: 600;
    font-size: 11px;
    text-align: center;
    height: 35px;
  }

  .cf-th.cf-sticky {
    z-index: 6;
  }

  .cf-th__code {
    display: block;
    font-weight: 700;
    line-height: 1.2;
  }

  .cf-th__name {
    display: block;
    max-width: 128px;
    margin: 1px auto 0;
    font-size: 9px;
    font-weight: 400;
    line-height: 1.2;
    white-space: normal;
    opacity: 0.85;
  }

  .cf-th--tooltip::after {
    content: attr(data-tooltip);
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translate(-50%, 6px);
    background: #24272b;
    color: #fff;
    padding: 5px 9px;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 400;
    text-transform: none;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.08s ease-out;
    z-index: 20;
  }

  .cf-th--tooltip:hover::after {
    opacity: 1;
    visibility: visible;
  }

  .cf-sticky {
    position: sticky;
    background: var(--cf-sticky-bg);
  }

  .cf-sticky--1 {
    left: 0;
    z-index: 3;
    min-width: var(--cf-sticky-1);
    max-width: var(--cf-sticky-1);
    width: var(--cf-sticky-1);
  }

  .cf-sticky--2 {
    left: var(--cf-sticky-2-left);
    z-index: 3;
    min-width: var(--cf-sticky-2);
    max-width: var(--cf-sticky-2);
    width: var(--cf-sticky-2);
  }

  .cf-th.cf-sticky--1,
  .cf-th.cf-sticky--2 {
    background: var(--cf-bg-head) !important;
  }

  .cf-col-sumber {
    min-width: 128px;
    text-align: center;
  }

  .cf-col-total,
  .cf-col-trx {
    min-width: 120px;
    text-align: center;
  }

  .cf-col-name {
    white-space: normal;
    min-width: var(--cf-sticky-2);
    max-width: var(--cf-sticky-2);
  }

  .cf-num {
    font-variant-numeric: tabular-nums;
    text-align: right;
  }

  .cf-num--bold {
    font-weight: 700;
  }

  .cf-num--zero {
    color: var(--cf-zero);
  }

  .cf-td--indent {
    padding-left: 26px;
  }

  .cf-td--section,
  .cf-td--label {
    font-weight: 700;
    white-space: nowrap;
  }

  .cf-td--label-wrap {
    white-space: normal !important;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.35;
    height: auto !important;
    min-height: 26px;
    padding-top: 6px;
    padding-bottom: 6px;
    vertical-align: middle;
  }

  .cf-row--subtotal .cf-td--label-wrap {
    text-align: left;
  }

  .cf-row--subtotal .cf-td--label,
  .cf-row--category_total .cf-td--label {
    padding-left: 26px;
  }

  .cf-td--section[colspan="2"],
  .cf-td--label[colspan="2"] {
    left: 0;
    z-index: 3;
    min-width: calc(var(--cf-sticky-1) + var(--cf-sticky-2));
    max-width: calc(var(--cf-sticky-1) + var(--cf-sticky-2));
    width: calc(var(--cf-sticky-1) + var(--cf-sticky-2));
  }

  /* —— Tree node —— */
  .cf-row--group .cf-td--section {
    cursor: pointer;
    user-select: none;
  }

  .cf-node {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .cf-node__chevron {
    color: var(--cf-text) !important;
    flex: 0 0 auto;
  }

  .cf-node__label {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .cf-node__count {
    flex: 0 0 auto;
    margin-left: auto;
    padding: 1px 6px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.7);
    color: var(--cf-text-muted);
    font-size: 10px;
    font-weight: 600;
  }

  .cf-row--type .cf-td--section {
    padding-left: 22px;
  }

  .cf-empty {
    text-align: center;
    padding: 48px 16px;
    color: var(--cf-text-muted);
    font-size: 14px;
  }

  .cf-row--category .cf-td {
    background: var(--cf-bg-category);
  }

  .cf-row--category:first-child .cf-td {
    border-top: none;
  }

  .cf-row--category:not(:first-child) .cf-td {
    border-top: 14px solid var(--cf-bg);
  }

  .cf-row--type .cf-td {
    background: var(--cf-bg-type);
  }

  .cf-row--subtotal .cf-td {
    background: var(--cf-bg-subtotal);
  }

  .cf-row--category_total .cf-td {
    background: var(--cf-bg-cat-total);
  }

  .cf-row--footer .cf-td {
    background: var(--cf-bg-footer);
  }

  .cf-row--footer_total .cf-td {
    background: var(--cf-bg-footer-total);
  }

  .cf-row--saldo-awal .cf-td,
  .cf-row--saldo-awal .cf-sticky {
    background: var(--cf-bg-saldo-awal);
    font-weight: 700;
  }

  .cf-row--saldo-akhir .cf-td,
  .cf-row--saldo-akhir .cf-sticky {
    background: var(--cf-bg-footer-total);
    font-weight: 700;
  }

  .cf-saldo-date {
    margin-left: 4px;
    font-size: 11px;
  }

  .cf-row--detail .cf-td,
  .cf-row--detail .cf-sticky {
    background: var(--cf-sticky-bg);
  }

  .cf-row--stripe .cf-td,
  .cf-row--stripe .cf-sticky {
    background: var(--cf-bg-stripe);
  }

  .cf-row--detail:hover .cf-td,
  .cf-row--detail:hover .cf-sticky {
    background: var(--cf-hover);
  }

  .cf-row--category.cf-row--group:hover .cf-td--section {
    background: #d0deee;
  }

  .cf-row--type.cf-row--group:hover .cf-td--section {
    background: #e3e9f1;
  }

  .cf-row--category .cf-sticky { background: var(--cf-bg-category); }
  .cf-row--type .cf-sticky { background: var(--cf-bg-type); }
  .cf-row--subtotal .cf-sticky { background: var(--cf-bg-subtotal); }
  .cf-row--category_total .cf-sticky { background: var(--cf-bg-cat-total); }
  .cf-row--footer .cf-sticky { background: var(--cf-bg-footer); }
  .cf-row--footer_total .cf-sticky { background: var(--cf-bg-footer-total); }

  .cf-row--category .cf-td--section {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.01em;
    padding-top: 10px;
    padding-bottom: 10px;
    text-transform: uppercase;
  }

  .cf-sticky--1 {
    box-shadow: 1px 0 0 var(--cf-border);
  }

  .cf-sticky--2 {
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
  }

  .cf-td--section[colspan="2"],
  .cf-td--label[colspan="2"] {
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 960px) {
    .cf-scroll {
      max-height: calc(100vh - 320px);
    }

    .cf-summary__item {
      min-width: calc(50% - 8px);
      flex: 1 1 calc(50% - 8px);
    }
  }

  @media (max-width: 600px) {
    .cf-summary__item {
      min-width: 100%;
      flex: 1 1 100%;
    }

    .cf-table {
      font-size: 11px;
    }

    .cf-col-sumber {
      min-width: 110px;
    }

    .cf-search__input {
      width: 140px;
    }
  }
</style>